import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { client, fetcher } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { timeAgo } from "../lib/community";
import { scheduleChangeText } from "../lib/format";

const label = (n) =>
  n.type?.startsWith("match_") ? scheduleChangeText({ type: n.type.slice(6), title: n.thread_title, new_date: n.new_date })
    : n.type === "upvote" ? `${n.actor_name} upvoted your post`
      : n.type === "reply_reply" ? `${n.actor_name} replied to your comment`
        : `${n.actor_name} replied to your thread`;

export const NotificationBell = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => fetcher("/community/notifications"),
    enabled: !!user,
    refetchInterval: 60000,
  });
  const markRead = useMutation({
    mutationFn: () => client.post("/community/notifications/read"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  if (!user) return null;
  const unread = data?.unread || 0;
  const items = data?.items || [];
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) markRead.mutate();
  };
  const go = (n) => { setOpen(false); nav(n.match_slug ? `/match/${n.match_slug}` : `/community/t/${n.thread_id}`); };
  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={toggle} aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 transition-colors hover:border-ink">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-badge"
            className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-volt px-1 text-[10px] font-black text-ink">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
          onMouseLeave={() => setOpen(false)} data-testid="notif-menu">
          <div className="border-b border-slate-100 px-4 py-2 font-display text-sm font-black">Notifications</div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">Nothing yet.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {items.map((n) => (
                <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => go(n)}
                  className={`flex w-full flex-col items-start gap-0.5 border-b border-slate-50 px-4 py-2.5 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-volt/5"}`}>
                  <span className="text-sm text-ink">{label(n)}</span>
                  <span className="w-full truncate text-xs text-slate-400">{n.match_slug ? "Fixture update" : n.thread_title} · {timeAgo(n.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
